import { useState, useEffect, useCallback, useMemo } from 'react';
import { BarChart3, Printer, Repeat, ChevronRight } from 'lucide-react';
import ModulLayout, { PANEL_OD } from '../../components/layout/ModulLayout';
import { stylySpizirny } from '../../lib/spizirnaUi';
import { ACCENT as SECTION_COLOR } from '../../lib/uiTokens';
import FinanceDokladyInbox from '../FinanceDokladyInbox';
import FinanceDokladyDetail from '../FinanceDokladyDetail';
import DokladPanelSloupec from './DokladPanelSloupec';
import DokladyStatistikyPanel from '../../components/doklady/DokladyStatistikyPanel';
import SablonyPanel from '../../components/doklady/SablonyPanel';
import DokladReportModal from '../DokladReportModal';
import ZpetTlacitko from '../../components/ui/ZpetTlacitko';

/** Co není doklad, ale do modulu patří. Stejná druhá skupina jako u Pokladny. */
const NAPRIC = [
    { id: 'statistiky', nazev: 'Statistiky', popis: 'výdaje po kategoriích a měsících', Icon: BarChart3 },
    { id: 'report', nazev: 'Tisk a report', popis: 'PDF za období a stavy', Icon: Printer },
    { id: 'sablony', nazev: 'Šablony', popis: 'fixní opakující se náklady', Icon: Repeat, jenCeo: true },
];

/**
 * Doklady v třísloupcovém standardu (`standard-rozvrzeni.md`).
 *
 *   seznam dokladů  |  doklad (hlavička + položky)  |  sken · zaúčtování · historie
 *
 * Předchozí podoba měla detail jako překryv: seznam se nezúžil, jen dostal
 * odsazení zprava, takže osm sloupců zůstalo v širokém rozvržení a namačkalo
 * se do zbytku. Panel statistik se přitom kreslil **jen** tehdy, když žádný
 * doklad otevřený nebyl — mizel přesně ve chvíli, kdy je člověk potřebuje
 * k rozhodnutí. Teď jsou statistiky položkou seznamu a třetí sloupec drží
 * sken, na který se člověk dívá u každé zapisované položky.
 */
export default function DokladyModul({ user, isDark = false, isMobile = false, rok, mesic, rezervaDole = null, initialDokladId = null }) {
    const isCeo = user?.role === 'superadmin';
    const isAdmin = isCeo || user?.role === 'admin';
    const { cardStyle, mutedColor, secondaryColor, inputStyle } = useMemo(
        () => stylySpizirny(isDark, SECTION_COLOR), [isDark],
    );
    const token = document.querySelector('meta[name="csrf-token"]')?.content;

    const [vybranyId, setVybranyId] = useState(initialDokladId);
    const [vybranoNapric, setVybranoNapric] = useState(null);
    const [zalozka, setZalozka] = useState('polozky');
    const [refreshKey, setRefreshKey] = useState(0);
    const [reportOpen, setReportOpen] = useState(false);
    const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

    // Vejde se třetí sloupec? Když ne, sken se stěhuje do detailu jako záložka.
    const [vejdeSePanel, setVejdeSePanel] = useState(() => window.innerWidth >= PANEL_OD);
    useEffect(() => {
        const h = () => setVejdeSePanel(window.innerWidth >= PANEL_OD);
        h();
        window.addEventListener('resize', h);
        return () => window.removeEventListener('resize', h);
    }, []);
    // Roztažením okna se obsah třetího sloupce (sken, zaúčtování, historie)
    // přestěhuje zpátky vedle a jeho záložky zmizí — detail by na nich zůstal
    // prázdný, takže se vrací na práci.
    useEffect(() => {
        if (vejdeSePanel && ['sken', 'zauctovani', 'historie'].includes(zalozka)) setZalozka('polozky');
    }, [vejdeSePanel, zalozka]);

    useEffect(() => { if (initialDokladId) { setVybranoNapric(null); setVybranyId(initialDokladId); } }, [initialDokladId]);

    const otevriDoklad = useCallback((id) => { setVybranoNapric(null); setVybranyId(id); }, []);
    const otevriNapric = useCallback((id) => {
        if (id === 'report') { setReportOpen(true); return; }   // report je modal, ne obsah sloupce
        setVybranyId(null);
        setVybranoNapric(id);
    }, []);
    const zpet = useCallback(() => { setVybranyId(null); setVybranoNapric(null); refresh(); }, [refresh]);

    // Ruční doklad — založit a rovnou otevřít k doplnění.
    const vytvoritRucne = async () => {
        try {
            const r = await fetch('/api/finance/doklady/manual', { method: 'POST', headers: { 'X-CSRF-TOKEN': token } });
            if (!r.ok) { const e = await r.json().catch(() => ({})); alert(e.message || 'Vytvoření dokladu selhalo'); return; }
            const d = await r.json();
            refresh();
            otevriDoklad(d.doklad_id);
        } catch { alert('Síťová chyba při vytváření dokladu'); }
    };

    const vybrano = vybranyId != null || vybranoNapric != null;
    const polozkyNapric = NAPRIC.filter(p => !p.jenCeo || isCeo);

    const panelSloupec = () => vybranyId != null ? (
        <DokladPanelSloupec dokladId={vybranyId} cardStyle={cardStyle} isDark={isDark}
            mutedColor={mutedColor} isAdmin={isAdmin} onZmena={refresh} />
    ) : null;

    // Uzka obrazovka: z tretiho sloupce jde do detailu jen karta skenu,
    // zauctovani a historie tam maji vlastni zalozky z dat, ktera detail uz ma.
    const skenSam = () => vybranyId != null ? (
        <DokladPanelSloupec dokladId={vybranyId} cast="sken" cardStyle={cardStyle} isDark={isDark}
            mutedColor={mutedColor} kompaktni />
    ) : null;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}
            data-ladit="doklady">
            <ModulLayout
                isMobile={isMobile}
                vybrano={vybrano}
                mobilRezim
                mobilPanel={false}
                mobilRezervaDole={rezervaDole}
                barva={SECTION_COLOR}
                isDark={isDark}
                seznam={({ siroky }) => (
                    <FinanceDokladyInbox
                        user={user} cardStyle={cardStyle} isDark={isDark}
                        rok={rok} mesic={mesic} refreshSignal={refreshKey}
                        selectedDokladId={vybranyId}
                        onOpenDetail={otevriDoklad}
                        onManualCreate={vytvoritRucne}
                        siroky={siroky} kompaktni={isMobile}
                        mutedColor={mutedColor} inputStyle={inputStyle}
                        rezervaDole={isMobile ? rezervaDole : null}
                        /* Nástroje modulu patří k nahrávání, ne na konec seznamu.
                           V Pokladně ta skupina funguje, protože fleky jsou čtyři
                           a je pořád vidět; tady by seděla pod čtyřmi sty doklady,
                           kam se nikdo nedoroluje.

                           Jako data, ne hotová nabídka: hlavička si je na mobilu
                           slije dohromady s akcemi seznamu do jediného „…", takže
                           nahoře zbyde hledání a filtr. */
                        nastrojeAkce={polozkyNapric.map(p => ({
                            id: p.id, label: p.nazev, Icon: p.Icon,
                            onClick: () => otevriNapric(p.id),
                        }))}
                    />
                )}
                detail={() => vybranoNapric ? (
                    <NapricDetail id={vybranoNapric} isDark={isDark} isMobile={isMobile} onZpet={zpet}
                        cardStyle={cardStyle} mutedColor={mutedColor} token={token}
                        rok={rok} mesic={mesic} refreshKey={refreshKey} onChange={refresh}
                        rezervaDole={isMobile ? rezervaDole : null} />
                ) : (
                    /* Plovoucí lišta leží NAD obsahem, takže poslední řádky detailu
                       končily pod ní a nedalo se k nim dorolovat. Rezerva patří
                       dovnitř scrollu, ne pod něj — obsah tak pod lištou normálně
                       projede a na konci je místo. */
                    <div className={isMobile ? 'flex flex-col flex-1 min-h-0 overflow-y-auto' : 'rounded-xl overflow-y-auto flex flex-col flex-1 min-h-0'}
                        style={isMobile ? { paddingBottom: rezervaDole || undefined } : cardStyle}>
                        <FinanceDokladyDetail
                            key={vybranyId}
                            dokladId={vybranyId}
                            user={user} cardStyle={cardStyle} isDark={isDark}
                            inspector inspectorWidth={isMobile ? window.innerWidth : 760}
                            zalozka={zalozka} setZalozka={setZalozka}
                            skenNode={!vejdeSePanel ? skenSam() : null}
                            onBack={zpet}
                            onOpenDoklad={otevriDoklad}
                            onZmena={refresh}
                        />
                    </div>
                )}
                panel={() => panelSloupec()}
            />

            {reportOpen && (
                <DokladReportModal cardStyle={cardStyle} isDark={isDark} onClose={() => setReportOpen(false)} />
            )}
        </div>
    );
}

function NapricDetail({ id, isDark, isMobile, onZpet, cardStyle, mutedColor, token, rok, mesic, refreshKey, onChange, rezervaDole = null }) {
    const meta = NAPRIC.find(p => p.id === id);
    const hranice = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    return (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'}
            style={isMobile ? null : cardStyle}>
            <div className="flex items-center gap-3" style={{ padding: isMobile ? '8px 12px 10px' : 16, borderBottom: `1px solid ${hranice}` }}>
                {isMobile && (
                    <ZpetTlacitko onClick={onZpet} popis="Zpět na seznam" />
                )}
                {meta?.Icon && !isMobile && (
                    <span className="inline-flex items-center justify-center flex-shrink-0"
                        style={{ width: 44, height: 44, borderRadius: 10, background: `${SECTION_COLOR}18`, border: `1px solid ${SECTION_COLOR}33`, color: SECTION_COLOR }}>
                        <meta.Icon size={20} />
                    </span>
                )}
                <div className="min-w-0">
                    <div className="text-[16px] font-bold truncate" style={{ color: 'var(--text-primary)' }}>{meta?.nazev}</div>
                    <div className="text-[12px]" style={{ color: mutedColor }}>{meta?.popis}</div>
                </div>
            </div>
            <div className="flex-1 overflow-y-auto" style={{ padding: 12, paddingBottom: rezervaDole || 12 }}>
                {id === 'statistiky' && <DokladyStatistikyPanel rok={rok} mesic={mesic} refreshKey={refreshKey} cardStyle={cardStyle} />}
                {id === 'sablony' && <SablonyPanel cardStyle={cardStyle} isDark={isDark} token={token} onChange={onChange} />}
            </div>
        </div>
    );
}
